/**
 * 大分県の背景ツアー「大分めぐり」コンテンツ。
 *
 * 軸は時間帯。由布院の朝霧から始めて、別府の夜の湯けむりで終わる。
 * 10市町村に1か所ずつ散らしてあり、大分市は入れていない（別府も1か所だけ）。
 *
 * 宇佐神宮・富貴寺・臼杵石仏はいまも祈りの場だが、solemn は立てていない。
 * 既存の11県で厳島神社・太宰府天満宮・祐徳稲荷を通常の景色として扱っており、
 * 追悼・慰霊の場（原爆ドーム、平和公園、知覧）とは線を分けている。
 * ただし「パワースポット」「ご利益」の類の語は、公的出典にないので使わない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 耶馬渓「日本三大奇勝」、原尻の滝「東洋のナイアガラ」はどちらも公的資料になし。
 *  - 青の洞門を「禅海が一人で掘った」と書かない。中津市公式は「石工らと」。
 *  - 「日本最初の有料道路」も中津市公式が「とも言われている」。断定しない。
 *  - 岡城跡に建物は残っていない。「天守から一望」と書かない。
 *  - 「荒城の月のモデル」は他県も名乗っている。断定しない。
 *  - 別府「世界第2位の湧出量」は国の統計で裏が取れない。源泉数は環境省統計で書ける。
 *  - 由布院温泉の「湧出量・源泉数 全国2位」は母集団が明示されていないので使わない。
 *  - 表記は「湯布院」ではなく「由布院」。自治体名は由布市。
 *  - 臼杵石仏は「磨崖仏として全国初の国宝」。「日本最古の磨崖仏」ではない。
 *  - 富貴寺大堂は「現存する九州最古の木造建築」。九州という限定を落とさない。
 */

import type { StageTour } from '../types';

export const oitaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'kinrinko',
      name: '金鱗湖の朝霧',
      area: '由布市・冬の早朝',
      image: '/assets/scenery/oita/scenes/01_kinrinko.webp',
      hotspots: [
        {
          id: 'kinrin-name',
          x: 0.46,
          y: 0.36,
          title: '夕日で鱗が金に光る湖',
          body: '金鱗湖の名前は、明治初期の儒学者・毛利空桑がつけたといわれています。湖で泳ぐ魚の鱗が夕日で金色に輝くのを見たことが由来です。',
          source: 'https://www.city.yufu.oita.jp/kankou/kankou-2/kankou_cate1_1/kinrinko',
          mapQuery: '金鱗湖',
        },
        {
          id: 'kinrin-mist',
          x: 0.7,
          y: 0.46,
          title: '温泉と清水が流れこむ',
          body: '金鱗湖には清水と温泉の両方が流れこんでいるといわれ、一年を通して水温が高く保たれています。冬の早朝、外の気温が下がると湖面から湯気が立ちのぼります。',
          source: 'https://www.city.yufu.oita.jp/kankou/kankou-2/kankou_cate1_1/kinrinko',
          relatedItemId: 'yaseuma',
          mapQuery: '金鱗湖',
        },
      ],
    },
    {
      id: 'usa-jingu',
      name: '宇佐神宮',
      area: '宇佐市・朝',
      image: '/assets/scenery/oita/scenes/02_usa_jingu.webp',
      hotspots: [
        {
          id: 'usa-sohongu',
          x: 0.5,
          y: 0.34,
          title: '全国4万600社の総本宮',
          body: '全国におよそ11万ある神社のうち、4万600社あまりが八幡社です。宇佐神宮はその総本宮で、725年に今の場所に建てられました。天皇の使いが遣わされる勅祭社でもあります。',
          source:
            'https://www.city.usa.oita.jp/tourist/touristspot/touristspot2/touristspot3/10171.html',
          mapQuery: '宇佐神宮',
        },
        {
          id: 'usa-nihai',
          x: 0.26,
          y: 0.44,
          title: '二拝四拍手一拝',
          body: '宇佐神宮では、二拝、四拍手、一拝という作法が古くからの習わしとされています。一般的な参拝作法より、拍手が2回多いのが特徴です。',
          source: 'https://www.usa-kanko.jp/pages/116/',
          mapQuery: '宇佐神宮',
        },
        {
          id: 'usa-hachimanzukuri',
          x: 0.72,
          y: 0.26,
          title: '屋根を二つ並べた国宝',
          body: '本殿は「八幡造」という古い様式で、国宝に指定されています。切妻造の建物を前後に二つ並べ、中央の谷に大きな樋を設けた形です。',
          source: 'https://online.bunka.go.jp/heritages/detail/187746',
          mapQuery: '宇佐神宮',
        },
      ],
    },
    {
      id: 'ao-no-domon',
      name: '青の洞門と競秀峰',
      area: '中津市・午前',
      image: '/assets/scenery/oita/scenes/03_ao_no_domon.webp',
      hotspots: [
        {
          id: 'domon-zenkai',
          x: 0.4,
          y: 0.5,
          title: '30年かけて掘られた道',
          body: '禅海和尚は享保20年に洞門を掘ることを誓い、托鉢で資金を集めて石工たちとノミと鎚で掘りはじめました。30年あまり経った明和元年に完成し、全長は342メートル、トンネルの部分は144メートルあります。',
          source: 'https://www.city-nakatsu.jp/doc/2013080200107/',
          mapQuery: '青の洞門',
        },
        {
          id: 'yabakei-meisho',
          x: 0.66,
          y: 0.24,
          title: '5つの市と町にまたがる名勝',
          body: '耶馬渓は大正12年に国の名勝に指定されました。中津市を中心に、宇佐市・日田市・玖珠町・九重町にまたがり、東西およそ40キロ、南北およそ35キロに広がります。',
          source: 'https://oita-digitalzukan.jp/cultural_property/%E8%80%B6%E9%A6%AC%E6%B8%93/',
          mapQuery: '競秀峰',
        },
        {
          id: 'yabakei-nihonisan',
          x: 0.2,
          y: 0.3,
          title: '日本遺産「やばけい遊覧」',
          body: '2017年4月28日、耶馬渓一帯は「やばけい遊覧 大地に描いた山水絵巻の道をゆく」として日本遺産に認定されました。青の洞門と競秀峰がその構成に含まれています。',
          source: 'https://www.city-nakatsu.jp/doc/2017042500083/',
          mapQuery: '一目八景',
        },
      ],
    },
    {
      id: 'mameda',
      name: '日田 豆田町',
      area: '日田市・昼前',
      image: '/assets/scenery/oita/scenes/04_mameda.webp',
      hotspots: [
        {
          id: 'mameda-denken',
          x: 0.46,
          y: 0.42,
          title: '天領だった商人の町',
          body: '豆田町は江戸のはじめに城下町としてつくられ、寛永16年に日田が幕府の直轄地になると、役所の膝下町として栄えました。2004年12月10日に国の重要伝統的建造物群保存地区に選ばれています。',
          source: 'https://www.city.hita.oita.jp/soshiki/29/2428.html',
          mapQuery: '豆田町',
        },
        {
          id: 'mameda-machiwari',
          x: 0.74,
          y: 0.3,
          title: '南北2本・東西5本の町割',
          body: '町並みは南北2本の通りと東西5本の通りからなる整然とした町割で、江戸のはじめに城下町をつくったときの形をそのまま受けついでいます。',
          source: 'https://online.bunka.go.jp/heritages/detail/102249',
          relatedItemId: 'torimeshi',
          mapQuery: '豆田町',
        },
      ],
    },
    {
      id: 'tadehara',
      name: 'くじゅう連山とタデ原湿原',
      area: '九重町・昼',
      image: '/assets/scenery/oita/scenes/05_tadehara.webp',
      hotspots: [
        {
          id: 'tadehara-ramsar',
          x: 0.44,
          y: 0.46,
          title: '山の上にある珍しい湿原',
          body: 'くじゅう坊ガツル・タデ原湿原は、2005年11月8日にラムサール条約の登録湿地になりました。面積は91ヘクタールで、日本でも有数の山地の中間湿原です。',
          source: 'https://www.pref.oita.jp/soshiki/13070/ramsar.html',
          mapQuery: 'タデ原湿原',
        },
        {
          id: 'tadehara-noyaki',
          x: 0.72,
          y: 0.36,
          title: '野焼きが草原を守る',
          body: 'タデ原湿原では毎年、春のお彼岸のころに野焼きが行われます。草原は何もしないで放っておくと森林に移っていくため、野焼きによって草原の景色が保たれてきました。',
          source: 'https://www.pref.oita.jp/soshiki/13070/ramsar.html',
          mapQuery: 'タデ原湿原',
        },
        {
          id: 'tadehara-mokudo',
          x: 0.22,
          y: 0.5,
          title: '散策コースは2.5キロ',
          body: 'タデ原湿原の散策コースは、距離およそ2.5キロメートル、歩いて45分ほどです。木道の一部はバリアフリーになっています。入口の長者原ビジターセンターは入館無料です。',
          source: 'https://www.env.go.jp/nature/nationalparks/list/aso-kuju/course/01/',
          mapQuery: '長者原ビジターセンター',
        },
      ],
    },
    {
      id: 'harajiri',
      name: '原尻の滝',
      area: '豊後大野市・午後',
      image: '/assets/scenery/oita/scenes/06_harajiri.webp',
      hotspots: [
        {
          id: 'harajiri-kasairyu',
          x: 0.5,
          y: 0.42,
          title: '9万年前の火砕流が滝になった',
          body: '原尻の滝は、幅およそ120メートル、高さおよそ20メートルあります。9万年前の阿蘇山の大きな噴火で流れた火砕流が冷えて固まり、その岩が川に削られて崩れたことで、この形の滝ができました。',
          source: 'https://www.bungo-ohno.com/geosaite/',
          mapQuery: '原尻の滝',
        },
        {
          id: 'harajiri-torii',
          x: 0.24,
          y: 0.28,
          title: '滝の上に立つ鳥居',
          body: '滝の上流に立つ鳥居は、緒方三社のうち二宮社の鳥居です。毎年の川越しまつりでは、神輿がこの鳥居をくぐります。滝の上部は危険なため、近づかないよう呼びかけられています。',
          source: 'https://www.bungo-ohno.com/geosaite/',
          mapQuery: '原尻の滝',
        },
      ],
    },
    {
      id: 'okajo',
      name: '岡城跡',
      area: '竹田市・夕方前',
      image: '/assets/scenery/oita/scenes/07_okajo.webp',
      hotspots: [
        {
          id: 'okajo-ishigaki',
          x: 0.46,
          y: 0.4,
          title: '建物のない城',
          body: '岡城跡は1936年12月16日に国の史跡に指定されました。江戸時代を通じて岡藩主・中川家の居城でしたが、明治の廃藩置県のあとに建物は失われ、いまは石垣と櫓の跡、門の跡だけが残っています。',
          source: 'https://oita-digitalzukan.jp/cultural_property/%E5%B2%A1%E5%9F%8E%E8%B7%A1/',
          mapQuery: '岡城跡',
        },
        {
          id: 'okajo-325m',
          x: 0.72,
          y: 0.5,
          title: '石垣づくりの城',
          body: '岡城は標高325メートルの高さにそびえる城です。文禄3年（1594年）に豊臣秀吉の命でこの地に入った岡藩初代藩主・中川秀成が、石垣づくりの城にしました。',
          source: 'https://taketa.guide/spots/detail/07fb5cc5-fa6c-4eee-9b51-fea20d2b67fc',
          mapQuery: '岡城跡',
        },
        {
          id: 'okajo-kojo',
          x: 0.22,
          y: 0.3,
          title: '瀧廉太郎が過ごした地',
          body: '岡城は、作曲家の瀧廉太郎が少年時代を過ごした地として紹介されています。竹田市には、「荒城の月」の作曲で知られる瀧廉太郎の記念館があります。',
          source: 'https://okajou.jp/',
          mapQuery: '岡城跡',
        },
      ],
    },
    {
      id: 'fukiji',
      name: '富貴寺大堂',
      area: '豊後高田市 国東半島・夕',
      image: '/assets/scenery/oita/scenes/08_fukiji.webp',
      hotspots: [
        {
          id: 'fukiji-oldest',
          x: 0.48,
          y: 0.4,
          title: '九州に残る最古の木造建築とされる',
          body: '富貴寺の大堂は国宝に指定されています。豊後高田市によると、いま残っている九州の木造建築のなかで最も古い建物です。阿弥陀如来をまつるお堂です。',
          source: 'https://www.city.bungotakada.oita.jp/site/showanomachi/1251.html',
          mapQuery: '富貴寺',
        },
        {
          id: 'fukiji-usa',
          x: 0.74,
          y: 0.32,
          title: '宇佐神宮とつながる寺',
          body: '富貴寺は平安時代に、宇佐神宮の大宮司の氏寺として開かれた寺院です。境内には、梵字が刻まれた仁聞石や、鎌倉時代の笠塔婆、室町時代の国東塔が残ります。',
          source: 'https://www.city.bungotakada.oita.jp/site/showanomachi/1251.html',
          mapQuery: '富貴寺',
        },
      ],
    },
    {
      id: 'usuki-sekibutsu',
      name: '臼杵石仏',
      area: '臼杵市・日没前',
      image: '/assets/scenery/oita/scenes/09_usuki_sekibutsu.webp',
      hotspots: [
        {
          id: 'usuki-kokuho',
          x: 0.48,
          y: 0.38,
          title: '磨崖仏で全国初の国宝',
          body: '臼杵石仏は1995年6月15日に国宝になりました。崖に彫られた磨崖仏が国宝に指定されたのは全国で初めてのことです。2017年に金剛力士像2体が加わり、国宝は合わせて61体になりました。',
          source: 'https://sekibutsu.com/about/',
          mapQuery: '臼杵石仏',
        },
        {
          id: 'usuki-butto',
          x: 0.74,
          y: 0.46,
          title: '仏頭が元の位置に',
          body: '古園石仏の中尊・大日如来は、日本の石仏の中でも最高傑作の一つとされます。崩壊や破損が激しかったため、昭和33年から修復工事が進められ、1993年8月25日に大日如来の仏頭が元の位置にもどされました。',
          source: 'https://sekibutsu.com/about/',
          mapQuery: '古園石仏',
        },
      ],
    },
    {
      id: 'kannawa-yukemuri',
      name: '鉄輪の湯けむり',
      area: '別府市・夜',
      image: '/assets/scenery/oita/scenes/10_kannawa_yukemuri.webp',
      hotspots: [
        {
          id: 'beppu-keikan',
          x: 0.46,
          y: 0.4,
          title: '湯けむりは国の文化財',
          body: '別府市の鉄輪と明礬の一帯は、2012年9月19日に国の重要文化的景観に選ばれました。地中から出た高温の蒸気は装置で温泉水と分けられ、水は配管で町へ、蒸気は湯けむりとして空へ上がります。',
          source: 'https://www.city.beppu.oita.jp/gakusyuu/bunkazai/yukemuri_keikan_index.html',
          mapQuery: '別府 湯けむり展望台',
        },
        {
          id: 'beppu-gensen',
          x: 0.72,
          y: 0.28,
          title: '大分県の源泉は5,094',
          body: '環境省の調べでは、大分県の源泉の数は5,094、湧出量は毎分293,610リットルで、どちらも全国第1位です。そのうち別府市だけで2,831の源泉があります。',
          source: 'https://www.pref.oita.jp/site/onsen/onsen-date.html',
          mapQuery: '別府温泉',
        },
        {
          id: 'beppu-tenbodai',
          x: 0.22,
          y: 0.48,
          title: '展望台は住宅地のなか',
          body: '湯けむり展望台は住宅地のなかにあります。別府市は、近くに住む人の迷惑にならないよう配慮を呼びかけています。土・日・祝日の夜には湯けむりのライトアップがあります。',
          source: 'https://www.city.beppu.oita.jp/sisetu/sangyou_kankou/yukemuri.html',
          mapQuery: '湯けむり展望台',
        },
      ],
    },
  ],
};
